import type { FC } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { SpotlightTransition } from '@atlaskit/onboarding';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';

import { usePostOfficeExernalMessageProxy } from '@atlassian/post-office-context';

import { useCoordinationClient } from '@confluence/engagement-provider';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { PTO_PHASE_TWO_SPOTLIGHT_TARGET } from '@confluence/premium-onboarding/entry-points/useShouldCheckEligibilityForSpotlight.ts';

import { useShouldCheckEligibilityForSpotlight } from './hooks/useCheckSpotlightEligibility.tsx';

const AUTO_PROVISIONING_CONFLUENCE_UPGRADE_EP_MESSAGE = 'auto-provisioning-confluence-upgrade';

/* eslint-disable check-i18n-id-prefix/i18n-id-prefix-with-package-name-defineMessages */
const i18n = defineMessages({
	manageYourTrial: {
		id: 'experiment-auto-provisioning-edition-parity.manage-your-trial.title',
		defaultMessage: 'Manage your trial',
		description: 'Title shown for the confluence trial spotlight',
	},
	learnMoreDescription: {
		id: 'experiment-auto-provisioning-edition-parity.trial-description',
		defaultMessage: 'Learn about your Premium features and manage your trial at any time.',
		description: 'Description shown for the confluence trial spotlight',
	},
	okButton: {
		id: 'experiment-auto-provisioning-edition-parity.ok.button',
		defaultMessage: 'Ok',
		description: 'Ok button',
	},
	// Duplicated with package-prefix ids for valid i18n for switching from experiment to auto-provisioning, will switch after these messages are translated
	manageYourTrialTitle: {
		id: 'auto-provisioning-edition-parity.manage-your-trial.title',
		defaultMessage: 'Manage your trial',
		description: 'Title shown for the confluence trial spotlight',
	},
	trialDescription: {
		id: 'auto-provisioning-edition-parity.trial-description',
		defaultMessage: 'Learn about your Premium features and manage your trial at any time.',
		description: 'Description shown for the confluence trial spotlight',
	},
	okButtonLabel: {
		id: 'auto-provisioning-edition-parity.ok.button',
		defaultMessage: 'Ok',
		description: 'Ok button',
	},
});

type ConfluenceTrialUpgradeSpotlightProps = {
	onClose: () => void;
};

export const ConfluenceTrialSpotlight: FC<ConfluenceTrialUpgradeSpotlightProps> = ({ onClose }) => {
	const coordinationClient = useCoordinationClient();
	const { Spotlight } = useChoreographerAssets();
	const { formatMessage } = useIntl();
	const [shouldRenderSpotlight, setShouldRenderSpotlight] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isEligible = useShouldCheckEligibilityForSpotlight();
	const { hasMessageStarted, requestMessageStart, dismissMessage } =
		usePostOfficeExernalMessageProxy(AUTO_PROVISIONING_CONFLUENCE_UPGRADE_EP_MESSAGE);

	useEffect(() => {
		const fetchEligibility = async () => {
			if (isEligible) {
				if (fg('post_office_ep_migration_auto-provisioning-confl')) {
					requestMessageStart();
				} else {
					coordinationClient
						.start(AUTO_PROVISIONING_CONFLUENCE_UPGRADE_EP_MESSAGE)
						.then(async (data) => {
							setShouldRenderSpotlight(data);
						})
						.catch(async () => setShouldRenderSpotlight(false));
				}
			}
		};
		void fetchEligibility();
	}, [coordinationClient, isEligible, requestMessageStart]);

	useEffect(() => {
		if (hasMessageStarted) {
			setShouldRenderSpotlight(true);
		}
	}, [hasMessageStarted]);

	const closeModal = useCallback(async () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'confluenceTrialUpgradeSpotlightOkButton',
				source: 'confluenceTrialUpgradeSpotlight',
			},
		}).fire();
		if (fg('post_office_ep_migration_auto-provisioning-confl')) {
			dismissMessage();
		} else {
			void coordinationClient.stop(AUTO_PROVISIONING_CONFLUENCE_UPGRADE_EP_MESSAGE);
		}
		onClose();
		setShouldRenderSpotlight(false);
	}, [createAnalyticsEvent, onClose, coordinationClient, dismissMessage]);

	useEffect(() => {
		shouldRenderSpotlight &&
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'confluenceTrialUpgradeSpotlight',
				},
			}).fire();
	}, [createAnalyticsEvent, shouldRenderSpotlight]);

	const handleErrorBoundary = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action: 'unhandledConfluenceTrialUpgradeError',
				source: 'confluenceTrialUpgradeSpotlight',
				actionSubject: 'ui',
			},
		}).fire();
	}, [createAnalyticsEvent]);

	const actions = useMemo(
		() => [
			{
				onClick: closeModal,
				text: formatMessage(i18n.okButton),
				appearance: 'default' as const,
			},
		],
		[closeModal, formatMessage],
	);

	return (
		<>
			{shouldRenderSpotlight && (
				<TransparentErrorBoundary
					attribution={Attribution.GROWTH_STARBURST}
					onError={handleErrorBoundary}
				>
					<SpotlightTransition>
						<Spotlight
							target={PTO_PHASE_TWO_SPOTLIGHT_TARGET}
							key={PTO_PHASE_TWO_SPOTLIGHT_TARGET}
							shouldWatchTarget
							targetRadius={3}
							heading={formatMessage(i18n.manageYourTrial)}
							dialogPlacement="bottom left"
							dialogWidth={289}
							targetBgColor={token('color.icon.inverse')}
							actions={actions}
						>
							{formatMessage(i18n.learnMoreDescription)}
						</Spotlight>
					</SpotlightTransition>
				</TransparentErrorBoundary>
			)}
		</>
	);
};
